package org.catharsis.spendtel.feature.presentation.home

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.outlined.AutoAwesome
import androidx.compose.material.icons.outlined.Lock
import androidx.compose.material.icons.outlined.Timeline
import androidx.compose.material.icons.outlined.UploadFile
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.foundation.BorderStroke
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.catharsis.TVTally.theme.deepGreen
import org.catharsis.spendtel.feature.presentation.expensereport.SpendTelGradients
import org.catharsis.spendtel.feature.presentation.expensereport.SpendTelReportColors
import org.catharsis.spendtel.ui.components.AutoResizeText
import org.catharsis.spendtel.ui.layout.PlatformLayout

@Composable
fun HomeEmptyHero(
    modifier: Modifier = Modifier,
) {
    Box(
        modifier = modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(bottomStart = 36.dp, bottomEnd = 36.dp))
            .background(SpendTelGradients.personality)
            .padding(horizontal = 28.dp, vertical = 40.dp),
    ) {
        Box(
            modifier = Modifier
                .size(120.dp)
                .align(Alignment.TopEnd)
                .clip(CircleShape)
                .background(Color.White.copy(alpha = 0.1f)),
        )
        Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
            Text(
                text = "YOUR WRAPPED",
                style = MaterialTheme.typography.labelMedium,
                color = Color.White.copy(alpha = 0.7f),
                letterSpacing = 2.sp,
            )
            Text(
                text = "Unwrap your\nspending story",
                style = MaterialTheme.typography.displaySmall,
                fontWeight = FontWeight.Bold,
                color = Color.White,
                lineHeight = 40.sp,
            )
            Text(
                text = "Upload statements · ~2 min",
                style = MaterialTheme.typography.bodyLarge,
                color = Color.White.copy(alpha = 0.88f),
            )
        }
    }
}

@Composable
fun HomeQuickSteps(
    modifier: Modifier = Modifier,
) {
    Row(
        modifier = modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceEvenly,
    ) {
        HomeQuickStep(icon = Icons.Outlined.UploadFile, label = "Upload")
        HomeQuickStep(icon = Icons.Outlined.AutoAwesome, label = "Analyze")
        HomeQuickStep(
            icon = Icons.Outlined.Timeline,
            label = "Unwrap",
            gradient = SpendTelGradients.storyWarm,
        )
    }
}

@Composable
private fun HomeQuickStep(
    icon: ImageVector,
    label: String,
    gradient: Brush = SpendTelGradients.hero,
) {
    Column(
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        Box(
            modifier = Modifier
                .size(52.dp)
                .clip(RoundedCornerShape(16.dp))
                .background(gradient),
            contentAlignment = Alignment.Center,
        ) {
            Icon(
                imageVector = icon,
                contentDescription = null,
                tint = Color.White,
                modifier = Modifier.size(24.dp),
            )
        }
        Text(
            text = label,
            style = MaterialTheme.typography.labelMedium,
            fontWeight = FontWeight.SemiBold,
            color = SpendTelReportColors.ink,
        )
    }
}

@Composable
fun HomeUploadButton(
    label: String,
    isBusy: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Button(
        onClick = onClick,
        enabled = !isBusy,
        modifier = modifier
            .fillMaxWidth()
            .height(56.dp),
        shape = RoundedCornerShape(18.dp),
        colors = ButtonDefaults.buttonColors(
            containerColor = deepGreen,
            contentColor = Color.White,
        ),
    ) {
        Icon(
            imageVector = Icons.Outlined.UploadFile,
            contentDescription = null,
            modifier = Modifier.size(22.dp),
        )
        Text(
            text = label,
            modifier = Modifier.padding(start = 10.dp),
            fontWeight = FontWeight.Bold,
            fontSize = 16.sp,
        )
    }
}

@Composable
fun HomeNewStoryButton(
    isBusy: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Button(
        onClick = onClick,
        enabled = !isBusy,
        modifier = modifier
            .fillMaxWidth()
            .height(56.dp),
        shape = RoundedCornerShape(18.dp),
        colors = ButtonDefaults.buttonColors(
            containerColor = deepGreen,
            contentColor = Color.White,
        ),
    ) {
        Icon(
            imageVector = Icons.Default.Add,
            contentDescription = null,
            modifier = Modifier.size(22.dp),
        )
        Text(
            text = "New story",
            modifier = Modifier.padding(start = 8.dp),
            fontWeight = FontWeight.Bold,
            fontSize = 16.sp,
        )
    }
}

/** Floating primary action — only the green pill; no extra chrome or padding below. */
@Composable
fun HomeNewStoryBottomDock(
    isBusy: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    HomeNewStoryButton(
        isBusy = isBusy,
        onClick = onClick,
        modifier = modifier
            .fillMaxWidth()
            .padding(horizontal = PlatformLayout.screenHorizontalPadding),
    )
}

@Composable
fun HomePrivacyNote(
    modifier: Modifier = Modifier,
) {
    Row(
        modifier = modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.Center,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Icon(
            imageVector = Icons.Outlined.Lock,
            contentDescription = null,
            tint = SpendTelReportColors.inkMuted,
            modifier = Modifier.size(14.dp),
        )
        Text(
            text = "Encrypted · Private",
            modifier = Modifier.padding(start = 6.dp),
            style = MaterialTheme.typography.labelSmall,
            color = SpendTelReportColors.inkMuted,
        )
    }
}

@Composable
fun HomeStoriesHeader(
    reportCount: Int,
    modifier: Modifier = Modifier,
) {
    Row(
        modifier = modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.Bottom,
    ) {
        Column(
            modifier = Modifier
                .weight(1f)
                .padding(end = 12.dp),
            verticalArrangement = Arrangement.spacedBy(4.dp),
        ) {
            AutoResizeText(
                text = "Your Financial Stories",
                style = MaterialTheme.typography.headlineMedium,
                fontWeight = FontWeight.Bold,
                color = SpendTelReportColors.ink,
                maxFontSize = MaterialTheme.typography.headlineMedium.fontSize,
                minFontSize = 20.sp,
            )
            Text(
                text = "Tap to open · Hold to delete",
                style = MaterialTheme.typography.labelSmall,
                color = SpendTelReportColors.inkMuted,
            )
        }
        Surface(
            shape = RoundedCornerShape(12.dp),
            color = SpendTelReportColors.violet.copy(alpha = 0.12f),
        ) {
            Text(
                text = reportCount.toString(),
                modifier = Modifier.padding(horizontal = 14.dp, vertical = 8.dp),
                style = MaterialTheme.typography.titleLarge,
                fontWeight = FontWeight.Bold,
                color = SpendTelReportColors.violet,
            )
        }
    }
}

@Composable
fun HomeProcessingStrip(
    label: String,
    modifier: Modifier = Modifier,
) {
    Surface(
        modifier = modifier.fillMaxWidth(),
        shape = RoundedCornerShape(18.dp),
        color = Color.White,
    ) {
        Row(
            modifier = Modifier.padding(horizontal = 18.dp, vertical = 16.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(14.dp),
        ) {
            CircularProgressIndicator(
                modifier = Modifier.size(28.dp),
                color = SpendTelReportColors.violet,
                strokeWidth = 3.dp,
            )
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = label,
                    style = MaterialTheme.typography.bodyMedium,
                    fontWeight = FontWeight.SemiBold,
                    color = SpendTelReportColors.ink,
                )
                Text(
                    text = "Keep the app open",
                    style = MaterialTheme.typography.labelSmall,
                    color = SpendTelReportColors.inkMuted,
                )
            }
        }
    }
}

@Composable
fun HomeErrorText(
    message: String,
    modifier: Modifier = Modifier,
) {
    Text(
        text = message,
        modifier = modifier.fillMaxWidth(),
        color = SpendTelReportColors.increase,
        style = MaterialTheme.typography.bodySmall,
        fontWeight = FontWeight.Medium,
    )
}

@Composable
fun HomeEntitlementHint(
    message: String,
    modifier: Modifier = Modifier,
) {
    Surface(
        modifier = modifier.fillMaxWidth(),
        shape = RoundedCornerShape(14.dp),
        color = SpendTelReportColors.indigo.copy(alpha = 0.08f),
        border = BorderStroke(1.dp, SpendTelReportColors.indigo.copy(alpha = 0.18f)),
    ) {
        Row(
            modifier = Modifier.padding(horizontal = 16.dp, vertical = 12.dp),
            horizontalArrangement = Arrangement.spacedBy(10.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Icon(
                imageVector = Icons.Outlined.AutoAwesome,
                contentDescription = null,
                tint = SpendTelReportColors.indigo,
                modifier = Modifier.size(18.dp),
            )
            Text(
                text = message,
                style = MaterialTheme.typography.bodyMedium,
                fontWeight = FontWeight.SemiBold,
                color = SpendTelReportColors.indigo,
            )
        }
    }
}

@Composable
fun HomeEmptyStateLayout(
    isBusy: Boolean,
    showsProgress: Boolean,
    uploadProgressLabel: String?,
    uploadError: String?,
    onSelectStatements: () -> Unit,
    entitlementHint: String? = null,
    modifier: Modifier = Modifier,
) {
    Column(
        modifier = modifier.fillMaxWidth(),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        HomeEmptyHero()

        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 24.dp)
                .padding(top = 32.dp, bottom = 28.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(28.dp),
        ) {
            entitlementHint?.let { hint ->
                HomeEntitlementHint(message = hint)
            }

            HomeQuickSteps()

            if (showsProgress) {
                HomeProcessingStrip(
                    label = uploadProgressLabel ?: "Processing…",
                )
            }

            uploadError?.let { HomeErrorText(message = it) }

            HomeUploadButton(
                label = "Upload statements",
                isBusy = isBusy,
                onClick = onSelectStatements,
            )

            HomePrivacyNote()
        }
    }
}
